import type { ParsedWorksheet } from "@/types";
import {
  formatTableOrdinalLabel,
  getSelectedMappingWorksheets,
  isWorksheetSelected,
  resolveMappingRegionId,
} from "@/lib/mappingRegions";

/** True when a label is an extraction id, not a human sheet/table name. */
export function isInternalRegionLabel(value: string | null | undefined): boolean {
  return /region_group_/i.test(value?.trim() ?? "");
}

function usableSheetLabel(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? "";
  if (!trimmed || isInternalRegionLabel(trimmed)) return null;
  return trimmed;
}

function worksheetSheetLabel(ws: ParsedWorksheet): string | null {
  const sheets =
    ws.sheetNames
      ?.map((s) => s.trim())
      .filter(Boolean)
      .join(", ") ?? "";
  return usableSheetLabel(sheets);
}

/** Map extraction region_id → Excel sheet names when available. */
export function buildRegionSheetLabelLookup(
  worksheets: ParsedWorksheet[],
  selection?: Record<string, boolean>
): Map<string, string> {
  const map = new Map<string, string>();
  const hasSelection = selection != null && Object.keys(selection).length > 0;

  worksheets.forEach((ws, index) => {
    if (hasSelection && !isWorksheetSelected(ws, index, selection!)) {
      return;
    }
    if (ws.isMetadata) return;

    const regionId = resolveMappingRegionId(ws).trim();
    if (!regionId) return;

    const label = worksheetSheetLabel(ws);
    if (label) {
      map.set(regionId, label);
    }
  });

  if (map.size === 0 && !hasSelection) {
    for (const ws of getSelectedMappingWorksheets(worksheets, {})) {
      const regionId = resolveMappingRegionId(ws).trim();
      if (!regionId || map.has(regionId)) continue;
      const label = worksheetSheetLabel(ws);
      if (label) {
        map.set(regionId, label);
      }
    }
  }

  return map;
}

export function regionLabelForRun(
  regionId: string | null | undefined,
  tableIndex: number,
  lookup: Map<string, string>
): string {
  const trimmed = regionId?.trim();
  const sheets = trimmed ? usableSheetLabel(lookup.get(trimmed)) : null;
  return formatTableOrdinalLabel(tableIndex + 1, sheets);
}
